/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: loggingSettingsApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API calls for Logging Settings module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { LoggingSettings, ConsoleSettings, FileLogging } from '../types';

export interface LoggingSettingsResponse {
  success: boolean;
  data?: LoggingSettings;
  error?: string;
  message?: string;
}

export interface CreateLoggingSettingsPayload {
  console_settings: ConsoleSettings;
  file_logging: FileLogging;
  enabled: boolean;
}

/**
 * Get current logging settings
 */
export async function getCurrentLoggingSettings(
  tenantSlug: string,
  userId: number
): Promise<LoggingSettingsResponse> {
  const response = await fetch(buildApiUrl('/api/logging-settings/current'), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch logging settings' }));
    return {
      success: false,
      error: error.error || 'FETCH_ERROR',
      message: error.message || 'Failed to fetch logging settings'
    };
  }

  return await response.json();
}

/**
 * Create or update logging settings
 */
export async function createOrUpdateLoggingSettings(
  tenantSlug: string,
  userId: number,
  payload: CreateLoggingSettingsPayload
): Promise<LoggingSettingsResponse> {
  const response = await fetch(buildApiUrl('/api/logging-settings'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to save logging settings' }));
    return {
      success: false,
      error: error.error || 'SAVE_ERROR',
      message: error.message || 'Failed to save logging settings'
    };
  }

  return await response.json();
}

/**
 * Delete logging settings
 */
export async function deleteLoggingSettings(
  tenantSlug: string,
  userId: number
): Promise<{ success: boolean; error?: string; message?: string }> {
  const response = await fetch(buildApiUrl('/api/logging-settings'), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete logging settings' }));
    return {
      success: false,
      error: error.error || 'DELETE_ERROR',
      message: error.message || 'Failed to delete logging settings'
    };
  }

  return await response.json();
}

